import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { appSettings } from 'config/app';
import { WebSocket } from 'helpers/socket';
import Loading from 'components/modals/Loading';
import Header from 'components/navigation/Header';
import Footer from 'components/navigation/Footer';

const Layout = () => {
  const { pathname } = useLocation();

  const layoutConfig = {
    header: {
      hidden: {
        exact: ['/intro', '/login', '/validate', '/faq', '/locker/opened', '/403', '/404'],
        patterns: []
      },
      height: '15dvh'
    },
    footer: {
      hidden: {
        exact: ['/intro', '/login', '/validate'],
        patterns: ['/asset/details', '/asset/pickup', '/asset/review', '/locker/opened', '/403', '/404']
      },
      height: '15dvh'
    },
    body: {
      getHeight: (hasHeader, hasFooter) => {
        if (!hasHeader && !hasFooter) return '100dvh';
        if (!hasHeader && hasFooter) return '85dvh';
        if (hasHeader && !hasFooter) return '85dvh';
        return '70dvh';
      }
    }
  };

  const matchesPattern = (path, patterns) => {
    if (patterns.exact.includes(path)) return true;

    return patterns.patterns.some(pattern => path.startsWith(pattern));
  };

  const showHeader = !matchesPattern(pathname, layoutConfig.header.hidden);
  const showFooter = !matchesPattern(pathname, layoutConfig.footer.hidden);

  const bodyHeight = layoutConfig.body.getHeight(showHeader, showFooter);

  return (
    <div className="max-w-lg bg-zinc-400 w-full m-auto">
      <div className="flex flex-col w-full bg-white h-dvh relative text-sm font-light overflow-hidden">
        <Loading />
        <ToastContainer position={appSettings.toast.position} />
        <WebSocket />

        {showHeader && (
          <div className="w-full px-8 z-20 relative flex" style={{ height: layoutConfig.header.height }}>
            <Header />
          </div>
        )}

        <div className="flex z-20 relative text-black" style={{ height: bodyHeight }}>
          <AnimatePresence mode="popLayout" initial={false}>
            <AnimatedOutlet />
          </AnimatePresence>
        </div>

        {showFooter && (
          <div className="w-full z-20 relative flex" style={{ height: layoutConfig.footer.height }}>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

const AnimatedOutlet = React.forwardRef((props, ref) => {
  const location = useLocation();

  return (
    <motion.div
      ref={ref}
      key={location.pathname}
      initial={{ opacity: 0, x: '100%' }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: '-100%' }}
      transition={{ duration: 0.6, type: 'spring', bounce: 0 }}
      className="z-20 w-full overflow-y-auto"
      {...props}
    >
      <Outlet />
    </motion.div>
  );
});

export default Layout;